import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import ConsoleLayout from './ConsoleLayout';
import { useAuth } from '../../context/AuthContext';
import { consoleTablesApi } from '../../services/api/console/tables.api';
import { ConsoleManagedTable, ConsoleTableProfileColumn } from '../../types';

const wordCount = (textValue: string) => {
  return (textValue.match(/\b\w+\b/g) || []).length;
};

const parsePermissionEmails = (rawValue: string): string[] => {
  const items = rawValue
    .split(/[\n,;]/)
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry.length > 0);
  return Array.from(new Set(items));
};

const ratioAsPercent = (ratio: number | undefined) => {
  if (ratio === undefined || ratio === null || Number.isNaN(ratio)) return '-';
  return `${(ratio * 100).toFixed(2)}%`;
};

const ConsoleTables: React.FC = () => {
  const { user } = useAuth();
  const requesterEmail = user?.email || user?.userId || '';

  const [managedTables, setManagedTables] = useState<ConsoleManagedTable[]>([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [description, setDescription] = useState('');
  const [permissionEmailsInput, setPermissionEmailsInput] = useState('');

  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profilesByTable, setProfilesByTable] = useState<Record<number, ConsoleTableProfileColumn[]>>({});
  const [columnFieldDraftByTable, setColumnFieldDraftByTable] = useState<
    Record<number, Record<string, { field_values: Record<string, string>; saving?: boolean }>>
  >({});
  const [activeLoadingTableId, setActiveLoadingTableId] = useState<number | null>(null);
  const [profileActionMessage, setProfileActionMessage] = useState('');
  const [approvingTableId, setApprovingTableId] = useState<number | null>(null);

  const selectedTable = useMemo(
    () => managedTables.find((item) => item.table_id === Number(selectedTableId)),
    [managedTables, selectedTableId]
  );

  const hydrateForm = useCallback((table?: ConsoleManagedTable) => {
    if (!table) {
      setDescription('');
      setPermissionEmailsInput('');
      return;
    }

    setDescription(table.table_description || '');
    setPermissionEmailsInput((table.permission_emails || []).join('\n'));
  }, []);

  const loadPageData = useCallback(async () => {
    if (!requesterEmail || !user?.canAccessConsole) {
      return;
    }

    setLoading(true);
    try {
      const tables = await consoleTablesApi.getConfiguredTables(requesterEmail);
      setManagedTables(tables);

      const currentSelectionExists = tables.some((item) => String(item.table_id) === selectedTableId);
      if (!currentSelectionExists && tables.length) {
        setSelectedTableId(String(tables[0].table_id));
        hydrateForm(tables[0]);
      } else if (currentSelectionExists) {
        const activeTable = tables.find((item) => String(item.table_id) === selectedTableId);
        hydrateForm(activeTable);
      } else {
        setSelectedTableId('');
        hydrateForm(undefined);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load table data');
    } finally {
      setLoading(false);
    }
  }, [requesterEmail, selectedTableId, user?.canAccessConsole, hydrateForm]);

  useEffect(() => {
    void loadPageData();
  }, [loadPageData]);

  useEffect(() => {
    hydrateForm(selectedTable);
  }, [selectedTable, hydrateForm]);

  const handleSaveSettings = async (event: FormEvent) => {
    event.preventDefault();
    if (!requesterEmail || !selectedTable) {
      toast.error('Select a table first');
      return;
    }

    if (wordCount(description) < 200) {
      toast.error('Table description must be at least 200 words');
      return;
    }

    setSavingSettings(true);
    try {
      await consoleTablesApi.updateTableSettings({
        requester_email: requesterEmail,
        table_id: selectedTable.table_id,
        table_description: description,
        aliases: selectedTable.aliases || [],
        permission_emails: parsePermissionEmails(permissionEmailsInput),
      });
      toast.success('Table settings updated');
      await loadPageData();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save table settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const loadProfile = useCallback(async (tableId: number) => {
    if (!requesterEmail) {
      return;
    }
    setActiveLoadingTableId(tableId);
    setProfileActionMessage(`Loading profile for table #${tableId}...`);
    try {
      const response = await consoleTablesApi.getTableProfile(tableId, requesterEmail);
      setProfilesByTable((prev) => ({ ...prev, [tableId]: response.table_profile || [] }));
      const drafts: Record<string, { field_values: Record<string, string>; saving?: boolean }> = {};
      (response.table_profile || []).forEach((row) => {
        drafts[row.column] = {
          field_values: row.custom_fields || {},
        };
      });
      setColumnFieldDraftByTable((prev) => ({ ...prev, [tableId]: drafts }));
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load profile');
    } finally {
      setActiveLoadingTableId(null);
      setProfileActionMessage('');
    }
  }, [requesterEmail]);

  useEffect(() => {
    if (!selectedTableId) {
      return;
    }
    const tableId = Number(selectedTableId);
    if (!profilesByTable[tableId]) {
      void loadProfile(tableId);
    }
  }, [selectedTableId, profilesByTable, loadProfile]);

  const recomputeProfile = async (tableId: number) => {
    if (!requesterEmail) {
      return;
    }
    const resetAll = window.confirm(
      `Recompute table #${tableId}: click OK to REMOVE all existing column-level data and restart, or Cancel to only sync added/removed columns.`
    );
    let recomputeMode: 'incremental_sync' | 'reset_all' = 'incremental_sync';
    let confirmDeleteText: string | undefined;
    if (resetAll) {
      const confirmationText = window.prompt(
        "Type DELETE to confirm full reset. Any other input will cancel."
      );
      if (confirmationText !== 'DELETE') {
        toast.info('Reset cancelled');
        return;
      }
      recomputeMode = 'reset_all';
      confirmDeleteText = confirmationText;
    } else {
      const proceed = window.confirm(
        'Proceed with incremental sync? This keeps existing data and only reflects schema changes.'
      );
      if (!proceed) {
        return;
      }
    }
    setActiveLoadingTableId(tableId);
    setProfileActionMessage(`Recomputing profile for table #${tableId}. This can take some time...`);
    try {
      const result = await consoleTablesApi.recomputeProfile(tableId, requesterEmail, {
        recompute_mode: recomputeMode,
        confirm_delete_text: confirmDeleteText,
      });
      await loadProfile(tableId);
      toast.success(
        `Profile v${result.profile_version} (${result.recompute_mode}). Added: ${result.added_columns.length}, removed: ${result.removed_columns.length}, changed: ${result.changed_columns.length}`
      );
      await loadPageData();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to recompute profile');
    } finally {
      setActiveLoadingTableId(null);
      setProfileActionMessage('');
    }
  };

  const approveTable = async (tableId: number, stage: 'developer' | 'admin') => {
    if (!requesterEmail) return;
    if (stage === 'admin' && !user?.isAdmin) {
      toast.error('Admin role required');
      return;
    }
    setApprovingTableId(tableId);
    try {
      await consoleTablesApi.approveTable({
        requester_email: requesterEmail,
        table_id: tableId,
        stage,
        is_approved: true,
      });
      await loadPageData();
      toast.success(stage === 'admin' ? 'Admin approval submitted' : 'Developer approval submitted');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to submit approval');
    } finally {
      setApprovingTableId(null);
    }
  };

  const toggleColumn = async (tableId: number, columnName: string, isActive: boolean) => {
    if (!requesterEmail) {
      return;
    }
    try {
      await consoleTablesApi.setColumnActive({
        requester_email: requesterEmail,
        table_id: tableId,
        column_name: columnName,
        is_active: !isActive,
      });

      setProfilesByTable((prev) => ({
        ...prev,
        [tableId]: (prev[tableId] || []).map((col) =>
          col.column === columnName ? { ...col, is_active: !isActive } : col
        ),
      }));
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to update column');
    }
  };

  const updateColumnFieldDraft = (tableId: number, columnName: string, fieldKey: string, value: string) => {
    setColumnFieldDraftByTable((prev) => ({
      ...prev,
      [tableId]: {
        ...(prev[tableId] || {}),
        [columnName]: {
          field_values: {
            ...(prev[tableId]?.[columnName]?.field_values || {}),
            [fieldKey]: value,
          },
          saving: prev[tableId]?.[columnName]?.saving || false,
        },
      },
    }));
  };

  const setColumnFieldSaving = (tableId: number, columnName: string, saving: boolean) => {
    setColumnFieldDraftByTable((prev) => ({
      ...prev,
      [tableId]: {
        ...(prev[tableId] || {}),
        [columnName]: {
          field_values: prev[tableId]?.[columnName]?.field_values || {},
          saving,
        },
      },
    }));
  };

  const saveColumnFieldValues = async (
    tableId: number,
    columnName: string,
    fieldKeys: string[]
  ) => {
    if (!requesterEmail) return;
    const currentValues = columnFieldDraftByTable[tableId]?.[columnName]?.field_values || {};
    const payloadValues = fieldKeys.reduce<Record<string, string>>((acc, key) => {
      acc[key] = currentValues[key] || '';
      return acc;
    }, {});

    setColumnFieldSaving(tableId, columnName, true);
    try {
      await consoleTablesApi.setColumnFieldValues({
        requester_email: requesterEmail,
        table_id: tableId,
        column_name: columnName,
        field_values: payloadValues,
      });
      setProfilesByTable((prev) => ({
        ...prev,
        [tableId]: (prev[tableId] || []).map((row) =>
          row.column === columnName ? { ...row, custom_fields: payloadValues } : row
        ),
      }));
      toast.success(`Saved fields for ${columnName}`);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save column field values');
    } finally {
      setColumnFieldSaving(tableId, columnName, false);
    }
  };

  if (!user?.canAccessConsole) {
    return (
      <ConsoleLayout title="Tables">
        <div className="console-card">
          <p>You do not have Console access.</p>
        </div>
      </ConsoleLayout>
    );
  }

  return (
    <ConsoleLayout title="Tables">
      <div className="console-grid two-col">
        <div className="console-card">
          <h2>Table Settings</h2>
          {!managedTables.length && !loading ? <p>No table assignments found.</p> : null}

          <form className="console-form" onSubmit={handleSaveSettings}>
            <label>
              Table
              <select value={selectedTableId} onChange={(e) => setSelectedTableId(e.target.value)} required>
                <option value="">Select table</option>
                {managedTables.map((item) => (
                  <option key={item.table_id} value={item.table_id}>
                    #{item.table_id} {item.snowflakes_table_name} ({item.postgres_table_name})
                  </option>
                ))}
              </select>
            </label>

            {selectedTable ? (
              <div className="console-subcard">
                <p>Primary: {selectedTable.primary_developer_email || '-'}</p>
                <p>Secondary: {selectedTable.secondary_developer_email || '-'}</p>
                <p>Daily Refresh: {selectedTable.daily_refresh ? 'Enabled' : 'Disabled'}</p>
              </div>
            ) : null}

            <label>
              Table Description ({wordCount(description)} words)
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Minimum 200 words"
                rows={8}
                required
              />
            </label>

            <label>
              Permission Emails
              <textarea
                value={permissionEmailsInput}
                onChange={(e) => setPermissionEmailsInput(e.target.value)}
                placeholder="one email per line, or comma separated"
                rows={5}
              />
            </label>

            <button type="submit" disabled={savingSettings || !selectedTableId}>
              {savingSettings ? 'Saving...' : 'Save Table Settings'}
            </button>
          </form>
        </div>

        <div className="console-card">
          <h2>Configured Tables</h2>
          {loading ? <p>Loading...</p> : null}

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Permissions</th>
                  <th>Updated By</th>
                </tr>
              </thead>
              <tbody>
                {managedTables.map((table) => (
                  <tr key={table.table_id}>
                    <td>
                      <div>
                        <strong>{table.snowflakes_table_name}</strong>
                      </div>
                      <div>{table.postgres_table_name}</div>
                    </td>
                    <td>{table.permission_emails?.length || 0}</td>
                    <td>{table.updated_by || '-'}</td>
                  </tr>
                ))}
                {!managedTables.length && !loading ? (
                  <tr>
                    <td colSpan={3}>No configured tables found.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {managedTables.map((table) => {
        const columnFieldSchema = table.column_field_schema || [];
        return (
        <div key={table.table_id} className="console-card">
          <div className="inline-header">
            <div>
              <strong>{table.snowflakes_table_name}</strong>
              <p>{table.postgres_table_name}</p>
              <p>
                Primary: {table.primary_developer_email} | Secondary: {table.secondary_developer_email}
              </p>
            </div>
            <div className="actions-row">
              <button
                type="button"
                onClick={() => loadProfile(table.table_id)}
                disabled={activeLoadingTableId === table.table_id}
              >
                {activeLoadingTableId === table.table_id ? 'Loading...' : 'View Profile'}
              </button>
              <button
                type="button"
                onClick={() => recomputeProfile(table.table_id)}
                disabled={activeLoadingTableId === table.table_id}
              >
                {activeLoadingTableId === table.table_id ? 'Computing...' : 'Recompute Profile'}
              </button>
            </div>
          </div>

          {activeLoadingTableId === table.table_id && profileActionMessage ? (
            <div className="console-inline-loading">{profileActionMessage}</div>
          ) : null}

          <div className="version-actions">
            <span>Approval: {table.approval_status || 'draft'}</span>
            <button
              type="button"
              onClick={() => approveTable(table.table_id, 'developer')}
              disabled={approvingTableId === table.table_id}
            >
              {approvingTableId === table.table_id ? 'Submitting...' : 'Developer Approve'}
            </button>
            {user?.isAdmin ? (
              <button
                type="button"
                onClick={() => approveTable(table.table_id, 'admin')}
                disabled={approvingTableId === table.table_id}
              >
                {approvingTableId === table.table_id ? 'Submitting...' : 'Admin Approve'}
              </button>
            ) : null}
          </div>

          {profilesByTable[table.table_id]?.length ? (
            <details className="console-collapse">
              <summary>Column Profile ({profilesByTable[table.table_id].length} columns)</summary>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Assigned Fields</th>
                      <th>Type</th>
                      <th>Format</th>
                      <th>Nullable</th>
                      <th>Distinct</th>
                      <th>Null %</th>
                      <th>Uniqueness</th>
                      <th>Values</th>
                      <th>Active</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profilesByTable[table.table_id].map((col) => (
                      <tr key={`${table.table_id}-${col.column}`}>
                        <td>{col.column}</td>
                        <td>
                          {columnFieldSchema.length ? (
                            <details>
                              <summary>Edit</summary>
                              <div className="column-fields-stack">
                                {columnFieldSchema.map((field) => (
                                  <label key={`${table.table_id}-${col.column}-${field.field_key}`}>
                                    <span>{field.field_label || field.field_key}</span>
                                    <input
                                      value={
                                        columnFieldDraftByTable[table.table_id]?.[col.column]?.field_values?.[
                                          field.field_key
                                        ] || ''
                                      }
                                      onChange={(e) =>
                                        updateColumnFieldDraft(
                                          table.table_id,
                                          col.column,
                                          field.field_key,
                                          e.target.value
                                        )
                                      }
                                      placeholder={field.field_key}
                                    />
                                  </label>
                                ))}
                                <button
                                  type="button"
                                  onClick={() =>
                                    void saveColumnFieldValues(
                                      table.table_id,
                                      col.column,
                                      columnFieldSchema.map((field) => field.field_key)
                                    )
                                  }
                                  disabled={!!columnFieldDraftByTable[table.table_id]?.[col.column]?.saving}
                                >
                                  {columnFieldDraftByTable[table.table_id]?.[col.column]?.saving
                                    ? 'Saving...'
                                    : 'Save'}
                                </button>
                              </div>
                            </details>
                          ) : (
                            <span>No column fields defined</span>
                          )}
                        </td>
                        <td>{col.type}</td>
                        <td>{col.format || '-'}</td>
                        <td>{col.is_nullable === undefined ? '-' : col.is_nullable ? 'Yes' : 'No'}</td>
                        <td>{col.distinct_count}</td>
                        <td>{ratioAsPercent(col.null_ratio)}</td>
                        <td>{ratioAsPercent(col.uniqueness_ratio)}</td>
                        <td>
                          <details>
                            <summary>
                              {(col.distinct_values || []).length}
                              {col.distinct_values_truncated ? '+' : ''} values
                            </summary>
                            <div className="column-values">
                              {(col.distinct_values || []).slice(0, 50).join(', ') || '-'}
                            </div>
                          </details>
                        </td>
                        <td>
                          <label className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={col.is_active}
                              onChange={() => toggleColumn(table.table_id, col.column, col.is_active)}
                            />
                            {col.is_active ? 'On' : 'Off'}
                          </label>
                        </td>
                        <td>
                          <details>
                            <summary>JSON</summary>
                            <pre style={{ whiteSpace: 'pre-wrap', minWidth: 320 }}>
                              {JSON.stringify(col, null, 2)}
                            </pre>
                          </details>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : null}
        </div>
      )})}
    </ConsoleLayout>
  );
};

export default ConsoleTables;
